import { Component, OnInit, OnChanges, SimpleChanges, Input, OnDestroy } from '@angular/core';
import { PerformCalculationsService } from 'app/shared/services/perform-calculations/perform-calculations.service';
import { CustomNumberFormatPipe, EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import Highcharts from 'highcharts';
import { DEFAULT_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-pie-chart',
    templateUrl: './pie-chart.component.html',
    styleUrls: ['./pie-chart.component.scss'],
    standalone: false
})
export class PieChartComponent implements OnInit, OnChanges, OnDestroy {

    @Input() labels: any;
    @Input() actual: any;
    @Input() units: string;
    @Input() level?: string;
    @Input() toolTip?: any;

    @Input() dontAnimate?;
    @Input() hiddenSeries = [];

    public chartValues: any;
    public chartDatasets: Array<any>;
    public chartLabels: Array<any>;
    public chartColors: Array<any> = [];
    public chartColors2: Array<any> = [];
    public popupTop;
    public popupLeft;
    public popupTitle;
    public popupContent: Array<any>;
    public fData;
    public fUnits: string;

    public chartOptions: Highcharts.Options;
    public highcharts = Highcharts;
    public chartRef: Highcharts.Chart | null = null;

    private unsubscriber = new ReplaySubject();

    constructor(
      private performCalculationsService: PerformCalculationsService,
      private customNumberPipe: CustomNumberFormatPipe,
      private decimalPipe: DecimalPipe,
      private themeService: ThemeService
    ) {}

    ngOnInit() {
        this.loadChartColors(EThemeName.LIGHT);
        if (this.level) {
            if (this.actual) {
                this.loadChartValues(this.labels, this.actual);
            }
        } else {
            if (this.actual) {
                const oom = this.performCalculationsService.getFinalOrder(this.actual);
                this.fUnits = this.performCalculationsService.getFinalUnits(oom, this.units);

                this.fData = this.performCalculationsService.applyOrder(this.actual, oom);
                this.loadChartValues(this.labels, this.fData);
            }
        }

        this.subscribeOnThemeChange();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.level) {
            if (this.actual) {
                this.loadChartValues(this.labels, this.actual);
            }
        } else {
            if (this.actual) {
                const oom = this.performCalculationsService.getFinalOrder(this.actual);
                this.fUnits = this.performCalculationsService.getFinalUnits(oom, this.units);
                this.fData = this.performCalculationsService.applyOrder(this.actual, oom);
                this.loadChartValues(this.labels, this.fData);
            }
        }
    }

    public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
      this.chartRef = chart;
    };

    private getValue = (value: number) => {
      if (!value) return '-'
      return this.customNumberPipe.transform(this.decimalPipe.transform(value, '1.1-1')) + ' MWh';
    }

    public loadChartOptions() {
      if (!this.chartDatasets || this.chartDatasets?.length === 0 || this.chartColors2?.length === 0) return;
      const getValue = this.getValue;
      const hasData = this.chartDatasets[0]?.data?.some(v => v !== 0);
      const data = this.chartDatasets[0]?.data?.map((a, i) => ({
        name: this.chartLabels[i],
        y: a,
        color: this.chartColors2[0]?.backgroundColor[i]
      })) || [];
      this.chartOptions = Highcharts.merge(DEFAULT_CHART_OPTIONS, {
        chart: {
          type: 'pie',
          height: hasData ? 300 : 1,
        },
        plotOptions: {
          series: {
            animation: { duration: 0 }
          }
        },
        legend: {
          layout: 'vertical',
          align: 'left',
          verticalAlign: 'middle',
          floating: false,
          x: 0,
          y: 0
        },
        tooltip: {
          enabled: true,
          outside: true,
          followPointer: true,
          hideDelay: 0,
          animation: { duration: 0 },
          useHTML: true,
          formatter: function() {
            return `<div style="font-size: 12px;">${this.point.name}: <span><b>${getValue(this.point.y)}</b></span></div>`;
          }
        },
        series: [{
          type: 'pie',
          name: '',
          dataLabels: { enabled: false },
          showInLegend: true,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false }
          },
          data: data
        }]
      })
    }

    public loadChartColors(t: EThemeName) {
        const borderColor = this.themeService.getColor('--card');

        this.chartColors = [{
            backgroundColor: [
                'rgba( 128,128, 128, 1)',
                this.themeService.getColor('--primary') + '80',
                'rgba(241,241,241,1)',
                'rgba(136,192,226,1)',
                'rgb(146, 97, 97)',
                'rgb(172, 130, 67)',
                'rgb(77, 67, 172)',
                'rgba(25, 90, 97, 0.78)',
                'rgba(83, 37, 105, 0.73)',
                'rgba(148, 146, 4, 0.58)',
                'rgba(245, 19, 19, 0.84)',
                'rgba(59, 124, 246, 0.84)'],
            borderColor: [borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor],
            borderWidth: [
                2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2
            ]
        }];

        this.chartColors2 = [{
            backgroundColor: [
                this.themeService.getColor('--red'),
                this.themeService.getColor('--kpi-normal'),
                'rgba( 128,128, 128, 1)',
                this.themeService.getColor('--primary') + '80',
                'rgba(241,241,241,1)',
                'rgba(136,192,226,0.5)',
                'rgb(146, 97, 97)',
                'rgb(172, 130, 67)',
                'rgb(77, 67, 172)',
                'rgba(25, 90, 97, 0.78)',
                'rgba(83, 37, 105, 0.73)',
                'rgba(148, 146, 4, 0.58)',
                'rgba(245, 19, 19, 0.84)',
                'rgba(59, 124, 246, 0.84)'],
            borderColor: [borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor,
                borderColor]
        }];
        if (this.hiddenSeries.length) {
            let index = this.hiddenSeries.includes('POS_IMBALANCE') ? 1 : 0;
            this.chartColors2[0].backgroundColor.splice(index, 1);
        }
    }

    public loadChartValues(labels, actual) {
        if (this.level === 'maintain' || this.level === 'maintain2') {
            const test = [];
            for (let i = 0; i < this.actual.length; i++) {
                if (this.toolTip) {
                    test.push({label: this.labels[i], value: this.actual[i], tooltip: this.toolTip[i]});
                } else {
                    test.push({label: this.labels[i], value: this.actual[i]});
                }
            }
            test.sort((a, b) => {
                return ((a.value > b.value) ? 1 : ((a.value < b.value) ? -1 : 0));
            });
            for (let i = 0; i < test.length; i++) {
                this.actual[i] = test[i].value;
                this.labels[i] = test[i].label;
                if (this.toolTip && test[i].tooltip) {
                    this.toolTip[i] = test[i].tooltip;
                }
            }
            if (this.toolTip) {
                const toolTip2 = this.toolTip.slice(0, 10);
                this.toolTip = toolTip2;

            }
            const actual2 = this.actual.slice(0, 10);
            const labels2 = this.labels.slice(0, 10);

            this.actual = actual2;
            this.labels = labels2;

            this.fUnits = this.units;
            this.chartLabels = labels;
            if (this.toolTip) {
                this.chartDatasets = [
                    {
                        label: 'Actual',
                        data: actual,
                        toolTip: this.toolTip
                    }
                ];
            } else {
                this.chartDatasets = [
                    {
                        label: 'Actual',
                        data: actual
                    }
                ];
            }
        } else if (this.level === 'forecast') {
            let index
            const actualCopy = cloneDeep(actual);
            const chartLabelsCopy = cloneDeep(labels);
            this.fUnits = this.units;

            if (this.hiddenSeries.length) {
                index = this.hiddenSeries.includes('POS_IMBALANCE') ? 1 : 0;
                chartLabelsCopy.splice(index, 1);
                actualCopy.splice(index, 1);
            }

            this.chartLabels = chartLabelsCopy;
            this.chartDatasets = [
                {
                    label: 'Actual',
                    data: actualCopy
                }
            ];
        } else {
            const actualDisplay = [];
            if (this.actual.length > 0) {
                for (let i = 0; i < this.actual.length; i++) {
                    actualDisplay[i] = (this.actual[i] / 1000000).toFixed(2);
                }
            }

            this.chartLabels = labels;
            this.chartDatasets = [
                {
                    label: 'Actual',
                    data: actualDisplay
                }
            ];
        }
        this.loadChartOptions();
    }

    ngOnDestroy() {
        this.unsubscriber.next(true);
        this.unsubscriber.complete();
    }

    private subscribeOnThemeChange(): void {
        this.themeService.getTheme().pipe(
            takeUntil(this.unsubscriber)
        ).subscribe((t) => {
          this.loadChartColors(t);
          this.loadChartValues(this.labels, this.actual);
        });
    }

}
